import { useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { useSSE } from './hooks/useSSE';
import { usePushNotifications } from './hooks/usePushNotifications';

import { THEMES, FONTS } from './lib/tokens';
import type { Theme } from './lib/tokens';
import type { MatchInfo } from './screens/Discover';
import { getStoredInviteToken, clearInviteToken } from './lib/invite';

// New screens
import { DiscoverScreen } from './screens/Discover';
import { MatchesScreen } from './screens/MatchesScreen';
import { MeScreen } from './screens/MeScreen';
import { ProfileDetail } from './screens/ProfileDetail';
import { Onboarding } from './screens/Onboarding';
import { MatchSheet } from './sheets/MatchSheet';
import { DateSheet } from './sheets/DateSheet';
import Auth from './pages/Auth';
import { TabBar, LogoMark, SoftButton } from './components/common';
import { PWAInstallBanner } from './components/PWAInstallBanner';
import { SplashScreen } from './components/SplashScreen';
import { ConsentBanner } from './components/ConsentBanner';
import { setUser, useScreenView } from './lib/analytics';

function BetaBand({ dark }: { dark: boolean }) {
  return (
    <div style={{
      flexShrink: 0,
      background: dark ? 'rgba(26,19,17,0.92)' : 'rgba(245,237,225,0.92)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderTop: `0.5px solid ${dark ? 'rgba(245,237,225,0.08)' : 'rgba(31,26,23,0.08)'}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 7,
      paddingTop: 5,
      paddingBottom: 'max(5px, env(safe-area-inset-bottom))',
    }}>
      <span style={{
        width: 5, height: 5, borderRadius: '50%',
        background: '#C8553D', opacity: 0.6, flexShrink: 0,
      }} />
      <span style={{
        fontFamily: '"Geist Mono", monospace',
        fontSize: 9,
        letterSpacing: '2px',
        textTransform: 'uppercase',
        color: 'rgba(200,85,61,0.55)',
        fontWeight: 500,
        lineHeight: 1,
      }}>
        Early Beta
      </span>
    </div>
  );
}

// ─── Partner invite confirm sheet ──────────────────────────────────
// For an already-registered user who clicks an invite link/code outside
// onboarding — same redeem endpoint, dead-simple slide-up confirm.

function PartnerInviteConfirmSheet({ t, inviterName, inviterPhoto, onJoin, onDismiss, joining }: {
  t: Theme; inviterName: string; inviterPhoto: string | null;
  onJoin: () => void; onDismiss: () => void; joining: boolean;
}) {
  const [show, setShow] = useState(false);
  useEffect(() => { const id = setTimeout(() => setShow(true), 40); return () => clearTimeout(id); }, []);

  return (
    <div
      onClick={onDismiss}
      style={{
        position: 'absolute', inset: 0, zIndex: 300,
        background: 'rgba(31,26,23,0.55)', backdropFilter: 'blur(8px)',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
        opacity: show ? 1 : 0, transition: 'opacity 200ms',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: t.paper, borderRadius: '28px 28px 0 0', padding: '20px 24px 32px',
          transform: show ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 360ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        }}
      >
        <div style={{ width: 40, height: 4, borderRadius: 2, background: t.line, margin: '0 auto 20px' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
          {inviterPhoto ? (
            <img src={inviterPhoto} alt="" style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
          ) : (
            <div style={{
              width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
              background: `linear-gradient(135deg, ${t.gold}66, ${t.terracotta}66)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 20, color: t.paper }}>{inviterName.charAt(0)}</span>
            </div>
          )}
          <div>
            <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 20, color: t.ink }}>{inviterName}</div>
            <div style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.inkMute, marginTop: 1 }}>invited you to pair up</div>
          </div>
        </div>
        <p style={{ fontFamily: FONTS.sans, fontSize: 14, color: t.inkSoft, lineHeight: 1.55, margin: '0 0 22px' }}>
          Joining switches you to couples mode and closes your open singles matches.
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <SoftButton t={t} variant="outline" onClick={onDismiss} style={{ flex: 1 }}>Not now</SoftButton>
          <SoftButton t={t} variant="terracotta" onClick={joining ? undefined : onJoin} style={{ flex: 1.3 }}>
            {joining ? 'Joining…' : 'Join as a couple'}
          </SoftButton>
        </div>
      </div>
    </div>
  );
}

type TabId = 'discover' | 'matches' | 'me';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,   // data stays fresh 5 min
      gcTime: 1000 * 60 * 30,     // keep in cache 30 min even if unused
      retry: 1,
      refetchOnWindowFocus: false, // don't refetch just from alt-tab
    },
  },
});

function LoadingScreen({ t }: { t: Theme }) {
  return (
    <div style={{
      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: t.paper,
    }}>
      <LogoMark size={52} animate />
    </div>
  );
}

// Shown when we're signed in but couldn't reach the profile endpoint. Explicitly
// reassures the user their account is intact — the previous behaviour dropped
// them into Onboarding, which looked like data loss.
function ConnectionTrouble({ t, onRetry }: { t: Theme; onRetry: () => Promise<void> }) {
  const [retrying, setRetrying] = useState(false);
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 32px', textAlign: 'center', background: t.paper }}>
      <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 28, color: t.ink, marginBottom: 10 }}>
        Can't reach Preminade
      </div>
      <div style={{ fontFamily: FONTS.sans, fontSize: 14, color: t.inkMute, lineHeight: 1.6, maxWidth: 280, marginBottom: 24 }}>
        Your account and matches are safe — we just couldn't load them. Check your connection and try again.
      </div>
      <button
        onClick={async () => { setRetrying(true); await onRetry(); setRetrying(false); }}
        disabled={retrying}
        style={{
          all: 'unset', cursor: retrying ? 'default' : 'pointer',
          padding: '14px 32px', borderRadius: 100,
          background: t.terracotta, color: t.paper,
          fontFamily: FONTS.sans, fontSize: 15, fontWeight: 600,
          opacity: retrying ? 0.6 : 1,
        }}
      >
        {retrying ? 'Retrying…' : 'Try again'}
      </button>
    </div>
  );
}

function ProtectedRoute({ children, t }: { children: React.ReactNode; t: Theme }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingScreen t={t} />;
  if (!isAuthenticated) return <Navigate to="/auth" replace />;
  return <>{children}</>;
}

// ─── Main App Shell ───────────────────────────────────────────────

interface MainAppProps {
  dark: boolean;
  onToggleTheme: () => void;
  t: Theme;
}

const SEEN_KEY = 'preminade_seen_matches';

function MainApp({ dark, onToggleTheme, t }: MainAppProps) {
  const { isOnboarded, userProfile, loading: authLoading, profileUnavailable, refreshProfile, getIdToken } = useAuth();
  const [tab, setTab] = useState<TabId>('discover');
  const [showSplash, setShowSplash] = useState(() => !sessionStorage.getItem('pm-splash-seen'));
  const [viewProfile, setViewProfile] = useState<any | null>(null);
  const [matchSheet, setMatchSheet] = useState<MatchInfo | null>(null);
  const [dateSheet, setDateSheet] = useState<MatchInfo | null>(null);
  const [newMatches, setNewMatches] = useState(false);
  const [pendingInvite, setPendingInvite] = useState<{ token: string; inviterName: string; inviterPhoto: string | null } | null>(null);
  const [joiningInvite, setJoiningInvite] = useState(false);
  const [inviteExpiredToast, setInviteExpiredToast] = useState(false);

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

  // One-time toast: an invitee's stored token turned out expired/invalid at
  // onboarding finish — they landed as a normal singles account instead.
  useEffect(() => {
    if (!sessionStorage.getItem('pm-invite-expired-toast')) return;
    sessionStorage.removeItem('pm-invite-expired-toast');
    setInviteExpiredToast(true);
    const id = setTimeout(() => setInviteExpiredToast(false), 5000);
    return () => clearTimeout(id);
  }, []);

  // Already-registered user clicking an invite link/code outside onboarding.
  useEffect(() => {
    const token = getStoredInviteToken();
    if (!token || !userProfile) return;
    fetch(`${API_BASE}/api/couples/invite/info?token=${encodeURIComponent(token)}`)
      .then(r => r.json())
      .then(data => {
        if (data.valid) {
          setPendingInvite({ token, inviterName: data.inviterName, inviterPhoto: data.inviterPhoto ?? null });
        } else {
          clearInviteToken();
        }
      })
      .catch(() => clearInviteToken());
  }, [userProfile?.id]);

  const handleJoinInvite = async () => {
    if (!pendingInvite) return;
    setJoiningInvite(true);
    try {
      const idToken = await getIdToken();
      const res = await fetch(`${API_BASE}/api/couples/invite/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ token: pendingInvite.token }),
      });
      clearInviteToken();
      setPendingInvite(null);
      if (res.ok) {
        await refreshProfile();
        queryClient.invalidateQueries({ queryKey: ['couple-me'] });
        queryClient.invalidateQueries({ queryKey: ['matches'] });
        queryClient.invalidateQueries({ queryKey: ['feed'] });
      }
    } catch {
      clearInviteToken();
      setPendingInvite(null);
    } finally {
      setJoiningInvite(false);
    }
  };

  const handleDismissInvite = () => {
    clearInviteToken();
    setPendingInvite(null);
  };

  // SSE: server pushes new_match / spark_revealed / date_plan_ready events.
  // The hook invalidates ['matches'] on these events; we also set the badge.
  useSSE(() => {
    // chat_message callback — not needed at app level, handled in ChatScreen
  });

  // Native push notifications (iOS / Android via Capacitor). No-op on web.
  usePushNotifications();

  // Prefetch feed + matches as soon as user is known
  useEffect(() => {
    if (!userProfile) return;
    const prefetch = async () => {
      const token = await getIdToken();
      if (!token) return;
      const headers = { Authorization: `Bearer ${token}` };
      queryClient.prefetchQuery({
        queryKey: ['feed'],
        queryFn: () => fetch(`${API_BASE}/api/matches/feed`, { headers }).then(r => r.json()).then(d => d.candidates),
        staleTime: 1000 * 60 * 5,
      });
      queryClient.prefetchQuery({
        queryKey: ['matches'],
        queryFn: () => fetch(`${API_BASE}/api/matches/active`, { headers }).then(r => r.json()).then(d => d.matches),
        staleTime: 1000 * 60 * 5,
      });
    };
    prefetch();
  }, [userProfile?.id]);

  // Check badge on mount and whenever matches query is invalidated (SSE triggers this)
  useEffect(() => {
    if (!userProfile) return;
    const check = async () => {
      try {
        const token = await getIdToken();
        if (!token) return;
        const res = await fetch(`${API_BASE}/api/matches/active`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const { matches } = await res.json();
        const ids: string[] = matches.map((m: any) => m.matchId);
        const seen: string[] = JSON.parse(localStorage.getItem(SEEN_KEY) || '[]');
        setNewMatches(ids.some((id: string) => !seen.includes(id)));
      } catch { /* silent */ }
    };
    check();
    // Subscribe to query cache updates so we react when SSE invalidates ['matches']
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && Array.isArray(event.query.queryKey) && event.query.queryKey[0] === 'matches') {
        check();
      }
    });
    return unsubscribe;
  }, [userProfile?.id]);

  // Analytics: who (internal ID, only sent with consent) and which screen.
  useEffect(() => {
    setUser(userProfile?.id ?? null, userProfile ? {
      mode: userProfile.mode === 'COUPLES' ? 'couples' : 'singles',
      university: userProfile.university ? 'yes' : 'no',
    } : {});
  }, [userProfile?.id, userProfile?.mode]);

  const TAB_SCREEN: Record<TabId, string> = { discover: 'discover', matches: 'pairs', me: 'you' };
  useScreenView(
    !userProfile || !isOnboarded ? null
      : matchSheet ? 'sheet/paired'
      : dateSheet ? 'sheet/date_plan'
      : viewProfile ? 'sheet/profile'
      : pendingInvite ? 'sheet/partner_invite'
      : TAB_SCREEN[tab],
  );

  const handleMatch = (m: MatchInfo) => setMatchSheet(m);

  const handleNavTab = (id: TabId) => {
    if (id === 'matches') {
      // Mark all current matches as seen
      getIdToken().then(token => {
        if (!token) return;
        fetch(`${API_BASE}/api/matches/active`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.json())
          .then(({ matches }) => {
            const ids = matches.map((m: any) => m.matchId);
            localStorage.setItem(SEEN_KEY, JSON.stringify(ids));
            setNewMatches(false);
          }).catch(() => {});
      });
    }
    setTab(id);
  };

  if (authLoading) {
    return (
      <div className="app-shell" style={{ background: t.paper }}>
        <LoadingScreen t={t} />
      </div>
    );
  }

  // We couldn't load the profile for a reason other than "no account" (rate
  // limit, 5xx, offline). Never fall through to Onboarding here — that shows an
  // existing user an empty app and reads as "my account was deleted".
  if (profileUnavailable && !isOnboarded) {
    return (
      <div className="app-shell" style={{ background: t.paper }}>
        <ConnectionTrouble t={t} onRetry={refreshProfile} />
      </div>
    );
  }

  if (!userProfile || !isOnboarded) {
    return (
      <div className="app-shell" style={{ background: t.paper }}>
        <Onboarding t={t} onFinish={async (opts) => {
          await refreshProfile();
          if (opts?.inviteExpired) {
            sessionStorage.setItem('pm-invite-expired-toast', '1');
          }
        }} />
      </div>
    );
  }

  return (
    <div className="app-shell" style={{ background: t.paper }}>
      {showSplash && (
        <SplashScreen t={t} onDone={() => { sessionStorage.setItem('pm-splash-seen', '1'); setShowSplash(false); }} />
      )}
      {/* Tab screens */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
        {tab === 'discover' && (
          <DiscoverScreen
            t={t}
            dark={dark}
            onMatch={handleMatch}
            onOpenProfile={setViewProfile}
          />
        )}
        {tab === 'matches' && (
          <MatchesScreen t={t} dark={dark} />
        )}
        {tab === 'me' && (
          <MeScreen t={t} dark={dark} onToggleTheme={onToggleTheme} />
        )}

        {/* Profile detail overlay */}
        {viewProfile && (
          <ProfileDetail
            profile={viewProfile}
            t={t}
            dark={dark}
            onBack={() => setViewProfile(null)}
            onLike={() => setViewProfile(null)}
            onPass={() => setViewProfile(null)}
          />
        )}

        {/* Match sheet */}
        {matchSheet && (
          <MatchSheet
            match={matchSheet}
            t={t}
            onClose={() => setMatchSheet(null)}
            onOpenDate={() => { setDateSheet(matchSheet); setMatchSheet(null); }}
          />
        )}

        {/* Date sheet */}
        {dateSheet && (
          <DateSheet
            match={dateSheet}
            t={t}
            onClose={() => setDateSheet(null)}
          />
        )}

        {/* Partner invite confirm — already-registered user clicked an invite link/code */}
        {pendingInvite && (
          <PartnerInviteConfirmSheet
            t={t}
            inviterName={pendingInvite.inviterName}
            inviterPhoto={pendingInvite.inviterPhoto}
            onJoin={handleJoinInvite}
            onDismiss={handleDismissInvite}
            joining={joiningInvite}
          />
        )}

        {/* One-time toast: stored invite turned out expired at onboarding finish */}
        {inviteExpiredToast && (
          <div style={{
            position: 'absolute', bottom: 90, left: '50%', transform: 'translateX(-50%)',
            background: t.ink, color: t.paper, borderRadius: 100,
            padding: '10px 18px', whiteSpace: 'nowrap', maxWidth: '85%',
            fontFamily: FONTS.sans, fontSize: 12.5, fontWeight: 500,
            zIndex: 400, boxShadow: t.shadow, textAlign: 'center',
          }}>
            That invite had expired — you're set up solo.
          </div>
        )}
      </div>

      <TabBar t={t} dark={dark} active={tab} onNav={handleNavTab} matchBadge={newMatches} />
      <BetaBand dark={dark} />
      <PWAInstallBanner t={t} />
    </div>
  );
}

// ─── App ─────────────────────────────────────────────────────────

function AppRoutes() {
  const [dark, setDark] = useState(false);
  const t = dark ? THEMES.dark : THEMES.light;

  return (
    <>
    <ConsentBanner t={t} />
    <Routes>
      <Route path="/auth" element={<Auth t={t} />} />
      {/* Dev route to test the Onboarding UI independently without login */}
      <Route path="/dev/onboarding" element={<div className="app-shell" style={{ background: t.paper }}><Onboarding t={t} onFinish={() => alert('Finished onboarding test!')} /></div>} />
      <Route path="/*" element={
        <ProtectedRoute t={t}>
          <MainApp dark={dark} onToggleTheme={() => setDark(d => !d)} t={t} />
        </ProtectedRoute>
      } />
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
